import React, { createContext, useContext } from 'react';
import { SubscriptionTier, TierLimits } from '../services/api';
import { TIER_FEATURES } from '../constants/subscription';
import { useAuth } from './AuthContext';

interface SubscriptionContextType {
  tier: SubscriptionTier;
  limits: TierLimits | null;
  hasRiskDetails: boolean;
  hasMessageBuilder: boolean;
  canUploadDocument: (currentCount: number) => boolean;
  canAskQuestion: (currentCount: number) => boolean;
  getTierFeatures: (tier: SubscriptionTier) => typeof TIER_FEATURES[SubscriptionTier];
  isFreeTier: boolean;
  isPremiumTier: boolean;
  isPlatinumTier: boolean;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();

  const normalizeTier = (val?: string | null): SubscriptionTier => {
    if (!val) return SubscriptionTier.FREE;
    const upper = val.toUpperCase();
    if (upper === SubscriptionTier.PREMIUM) return SubscriptionTier.PREMIUM;
    if (upper === SubscriptionTier.PLATINUM) return SubscriptionTier.PLATINUM;
    return SubscriptionTier.FREE;
  };

  const tier = normalizeTier(user?.subscription_tier);
  const limits = user?.subscription_limits || null;

  const hasRiskDetails = limits?.has_risk_details ?? false;
  const isFreeTier = tier === SubscriptionTier.FREE;
  const isPremiumTier = tier === SubscriptionTier.PREMIUM;
  const isPlatinumTier = tier === SubscriptionTier.PLATINUM;
  const hasMessageBuilder = isPremiumTier || isPlatinumTier;

  const canUploadDocument = (currentCount: number): boolean => {
    if (!limits) return false;
    return currentCount < limits.contracts_per_month;
  };

  const canAskQuestion = (currentCount: number): boolean => {
    if (!limits) return false;
    return currentCount < limits.questions_per_contract_month;
  };

  const getTierFeatures = (targetTier: SubscriptionTier) => {
    return TIER_FEATURES[targetTier];
  };

  return (
    <SubscriptionContext.Provider
      value={{
        tier,
        limits,
        hasRiskDetails,
        hasMessageBuilder,
        canUploadDocument,
        canAskQuestion,
        getTierFeatures,
        isFreeTier,
        isPremiumTier,
        isPlatinumTier,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within SubscriptionProvider');
  }
  return context;
};
